const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

export const isTypingTarget = (el) => {
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === "textarea" || tag === "select" || el.isContentEditable) return true;
  if (tag !== "input") return false;
  const type = (el.type || "text").toLowerCase();
  return !["button", "submit", "reset", "checkbox", "radio", "range", "file", "image", "color"].includes(type);
};

export const hasModifier = (e) => e.altKey || e.ctrlKey || e.metaKey;

export const clickable = (onClick, label) => ({
  role: "button",
  tabIndex: 0,
  "aria-label": label,
  onClick,
  onKeyDown: (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(e);
    }
  },
});

export const getFocusable = (container) =>
  container
    ? Array.from(container.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      )
    : [];

export const trapTab = (e, container) => {
  if (e.key !== "Tab") return;
  const items = getFocusable(container);
  if (items.length === 0) {
    e.preventDefault();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  if (e.shiftKey && (active === first || !container.contains(active))) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && (active === last || !container.contains(active))) {
    e.preventDefault();
    first.focus();
  }
};

const HOTSPOT = "[data-spot-focus]";

const isHotspotInView = (spot) => {
  const rect = spot.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return false;
  const left = Math.max(rect.left, 0);
  const right = Math.min(rect.right, window.innerWidth);
  const top = Math.max(rect.top, 0);
  const bottom = Math.min(rect.bottom, window.innerHeight);
  if (right <= left || bottom <= top) return false;

  const hit = document.elementFromPoint((left + right) / 2, (top + bottom) / 2);
  if (!hit) return false;
  const area = spot.closest(".hot-area");
  return spot.contains(hit) || (!!area && area.contains(hit));
};

export const syncHotspotTabOrder = (root = document) => {
  const visible = [];
  root.querySelectorAll(HOTSPOT).forEach((spot) => {
    const inView = isHotspotInView(spot);
    if (inView) visible.push(spot);
    spot.tabIndex = inView || spot === document.activeElement ? 0 : -1;
    spot.querySelectorAll(FOCUSABLE).forEach((el) => {
      el.tabIndex = -1;
    });
  });
  return visible;
};

export const handleHotspotTab = (e) => {
  if (e.key !== "Tab" || hasModifier(e)) return;
  const visible = syncHotspotTabOrder();
  const active = document.activeElement;

  if (active && active.matches && active.matches(HOTSPOT) && !visible.includes(active)) {
    active.tabIndex = -1;
    if (visible.length > 0) {
      e.preventDefault();
      visible[e.shiftKey ? visible.length - 1 : 0].focus({ preventScroll: true });
    }
  }
};

export const activateHotspot = (e) => {
  if (e.key !== "Enter" && e.key !== " ") return;
  if (e.target !== e.currentTarget) return;
  e.preventDefault();
  e.stopPropagation();
  const link = e.currentTarget.querySelector("a[href]");
  (link || e.currentTarget).click();
};
